// Wrapper-side Basic Auth prompt navigator (task).
//
// The SW's `routeThroughWrapper` (in `sw/core.ts`) emits a
// `p2claw_basic_auth_needed` message when an SPA-style
// (non-document, non-iframe) fetch returns 401-Basic and we don't
// have stored creds for the realm. Returning the form HTML body in
// that case would break the app's retry-on-401 (the page tries to
// JSON.parse our HTML and fails), so the SW returns the 401
// verbatim *and* asks the wrapper to navigate the iframe to
// `/__p2claw/login` — a destination the SW will intercept and serve
// the form for.
//
// De-dup on this side: if we're already navigating the iframe to
// the form, ignore subsequent messages until the navigation lands.
// The SW also de-dups per-realm, so two layers of guard catch the
// concurrent-fetch storm common in SPA boot sequences.

import type { SwContainerLike } from "./sw-handoff.js";

export interface BasicAuthPromptOptions {
  /** Same shape as `installSwHandoff`'s `navigatorSw` — usually
   *  `navigator.serviceWorker`. */
  navigatorSw: SwContainerLike;
  /** The app iframe (`#p2claw-app`). The listener mutates `iframe.src`. */
  iframe: HTMLIFrameElement;
  /** Returns the URL the iframe should redirect back to AFTER auth.
   *  Defaults to `window.location.pathname + window.location.search`,
   *  which mirrors the iframe's current logical URL via the wrapper's
   *  history-sync. Tests inject a deterministic stub. */
  currentIframeUrl?: () => string;
}

export interface BasicAuthPromptHandle {
  /** Tear down the listener. Idempotent. */
  dispose(): void;
}

const LOGIN_PATH = "/__p2claw/login";

/** Recognise the SW's prompt message shape; ignore everything else
 *  on the same `navigator.serviceWorker.message` channel
 *  (`p2claw_sw_wake`, plus any future entries). */
function isBasicAuthNeeded(d: unknown): d is { type: string; realm: string } {
  if (!d || typeof d !== "object") return false;
  const r = d as { type?: unknown; realm?: unknown };
  return r.type === "p2claw_basic_auth_needed" && typeof r.realm === "string";
}

/**
 * Install the listener. The handler navigates the iframe exactly
 * once per "burst" of `p2claw_basic_auth_needed` messages: a flag
 * set when the navigation starts, cleared once the iframe `load`
 * event fires (which happens both for the form load AND for the
 * subsequent post-auth redirect, so a NEW realm can prompt later).
 */
export function installBasicAuthPromptListener(
  opts: BasicAuthPromptOptions,
): BasicAuthPromptHandle {
  let navigating = false;
  let disposed = false;

  const onLoad = (): void => {
    navigating = false;
  };

  const getCurrentUrl = (): string =>
    opts.currentIframeUrl?.() ??
    (typeof window !== "undefined"
      ? window.location.pathname + window.location.search
      : "/");

  const onMessage = (ev: { data: unknown }): void => {
    if (disposed) return;
    if (!isBasicAuthNeeded(ev.data)) return;
    if (navigating) return;
    const realm = ev.data.realm;
    const next = getCurrentUrl();
    const target = `${LOGIN_PATH}?realm=${encodeURIComponent(realm)}&next=${encodeURIComponent(next)}`;
    navigating = true;
    // Wire the load listener BEFORE assigning src so we don't miss
    // the event on a synchronous render (test fakes do this).
    opts.iframe.addEventListener("load", onLoad, { once: true });
    opts.iframe.src = target;
  };

  opts.navigatorSw.addEventListener("message", onMessage);

  return {
    dispose(): void {
      if (disposed) return;
      disposed = true;
      opts.navigatorSw.removeEventListener("message", onMessage);
      opts.iframe.removeEventListener("load", onLoad);
    },
  };
}
